import {
  createContext,
  ReactNode,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { useLocalStorage } from "../hooks/use-local-storage";
import { useMediaQuery } from "usehooks-ts";
import { usePostHog } from "posthog-js/react";
import { useGetDecksLocal } from "@/lib/decks-utils/utils";
export type GuidedTourState = Record<string, boolean>;

// Dictionary of tour step priorities
export const GUIDED_TOUR_PRIORITIES: Record<string, number> = {
  // Define priorities for specific tour steps here
  // Example:
  // "onboarding-dashboard": 100,
  // "onboarding-sidebar": 90,
  // "feature-new-button": 80,
  "generate-flashcards-pdf": 100,
  "highlight-to-generate": 90,
  "image-occlusion": 80,
  "study-now": 10,
  "share-deck": -100,
  "type-answer-input": 60,
  "type-answer-input-confirm": 50,
  "spaced-repetition-feedback": 45,
  "spaced-repetition-feedback-button-hard": 45,
  "spaced-repetition-feedback-button-again": 45,
  "spaced-repetition-feedback-button-good": 45,
  "spaced-repetition-feedback-button-easy": 45,
  "show-answer-button": 40,
  "ai-helps": 10,

  "type-to-answer-toggle": 1,
};

// Default priority for steps not explicitly defined
const DEFAULT_PRIORITY = 0;

interface RegisteredTourStep {
  key: string;
  enabled: boolean;
}

interface GuidedTourControllerContextData {
  debounceFromPreviousStepPassed: boolean;
  isGuidedTourEnabled: boolean;
  setGuidedTourVisited: (guidedTourKey: string) => void;
  isGuidedTourVisited: (guidedTourKey: string) => boolean;
  registerTourStep: (key: string, enabled: boolean) => void;
  unregisterTourStep: (key: string) => void;
  isStepHighestPriority: (key: string) => boolean;
}

const GuidedTourControllerContext = createContext<
  GuidedTourControllerContextData | undefined
>(undefined);

GuidedTourControllerContext.displayName = "GuidedTourControllerContext";

export const useGuidedTourControllerContext = () => {
  const context = useContext(GuidedTourControllerContext);

  if (context === undefined) {
    throw new Error(
      "useGuidedTourContext must be used within a GuidedTourProvider"
    );
  }

  return context;
};

let hasStudiedEnoughCardsCache = false;
export const GuidedTourControllerProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isDebouncing, setIsDebouncing] = useState(false);
  const [guidedTourState, setGuidedTourState] =
    useLocalStorage<GuidedTourState>("guided-tour-v2", {});
  const [registeredSteps, setRegisteredSteps] = useState<RegisteredTourStep[]>(
    []
  );

  const localDecks = useGetDecksLocal();

  const posthog = usePostHog();

  const md = useMediaQuery("(min-width: 768px)");

  const highestPrioritySteps = useRef<string[]>([]);

  const isOnboardingEnabled = true;

  const hasStudiedEnoughCards = useMemo(() => {
    if (hasStudiedEnoughCardsCache) {
      return true;
    }

    if (!localDecks) {
      hasStudiedEnoughCardsCache = false;
      return false;
    }

    // If the user has 3 decks or more, we consider them to have studied enough cards
    if (localDecks.decks.length >= 3) {
      hasStudiedEnoughCardsCache = true;
      return true;
    }

    let cardCount = 0;
    for (const deck of localDecks.decks) {
      for (const card of deck.cards) {
        for (const state of card.studyState) {
          if (state.type === "good" || state.type === "easy") {
            cardCount += 1;
            if (cardCount >= 50) {
              hasStudiedEnoughCardsCache = true;
              return true;
            }
          }
        }
      }
    }
    return false;
  }, [localDecks]);

  const isGuidedTourEnabled =
    isOnboardingEnabled && md && !hasStudiedEnoughCards;

  // Calculate the highest priority steps that haven't been visited yet
  useEffect(() => {
    if (!isGuidedTourEnabled) {
      return;
    }

    const availableSteps = registeredSteps
      .filter((step) => step.enabled && !guidedTourState[step.key])
      .sort((a, b) => {
        // Get priorities from the dictionary, or use default
        const priorityA = GUIDED_TOUR_PRIORITIES[a.key] ?? DEFAULT_PRIORITY;
        const priorityB = GUIDED_TOUR_PRIORITIES[b.key] ?? DEFAULT_PRIORITY;
        return priorityB - priorityA; // Higher priority first
      });

    if (availableSteps.length === 0) {
      highestPrioritySteps.current = [];
      return;
    }

    // Find all steps that have the same highest priority
    const highestPriority =
      GUIDED_TOUR_PRIORITIES[availableSteps[0].key] ?? DEFAULT_PRIORITY;
    highestPrioritySteps.current = availableSteps
      .filter((step) => {
        const priority = GUIDED_TOUR_PRIORITIES[step.key] ?? DEFAULT_PRIORITY;
        return priority === highestPriority;
      })
      .map((step) => step.key);
  }, [registeredSteps, guidedTourState, isGuidedTourEnabled]);

  const isGuidedTourVisited = useCallback(
    (guidedTourKey: string) => {
      return !!guidedTourState[guidedTourKey] || !isGuidedTourEnabled;
    },
    [guidedTourState, isGuidedTourEnabled]
  );

  const setGuidedTourVisited = useCallback(
    (guidedTourKey: string) => {
      if (!isGuidedTourEnabled) {
        return;
      }

      setGuidedTourState((prev: GuidedTourState) => {
        const wasVisited = !!prev[guidedTourKey];

        // Only send analytics if not previously visited
        if (!wasVisited && isOnboardingEnabled) {
          posthog.capture(`Guided-Tour-v2-${guidedTourKey}-Visited`);
        }

        return {
          ...prev,
          [guidedTourKey]: true,
        };
      });

      // Clear any existing debounce timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Start debounce period
      setIsDebouncing(true);

      // Create a new debounce timeout
      const timeout = setTimeout(() => {
        setIsDebouncing(false);
      }, 1500); // 1.5 seconds debounce after deactivation

      debounceTimeoutRef.current = timeout;

      return () => {
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }
      };
    },
    [
      setGuidedTourState,
      isGuidedTourEnabled,
      guidedTourState,
      isOnboardingEnabled,
      posthog,
    ]
  );

  const registerTourStep = useCallback((key: string, enabled: boolean) => {
    setRegisteredSteps((prev) => {
      // Check if step already registered
      const existingIndex = prev.findIndex((step) => step.key === key);

      if (existingIndex >= 0) {
        // Update existing step
        const newSteps = [...prev];
        newSteps[existingIndex] = { key, enabled };
        return newSteps;
      } else {
        // Add new step
        return [...prev, { key, enabled }];
      }
    });
  }, []);

  const unregisterTourStep = useCallback((key: string) => {
    setRegisteredSteps((prev) => prev.filter((step) => step.key !== key));
  }, []);

  const isStepHighestPriority = useCallback((key: string) => {
    return highestPrioritySteps.current.includes(key);
  }, []);

  const contextValue = useMemo(
    () => ({
      debounceFromPreviousStepPassed: !isDebouncing,
      isGuidedTourEnabled,
      setGuidedTourVisited,
      isGuidedTourVisited,
      registerTourStep,
      unregisterTourStep,
      isStepHighestPriority,
    }),
    [
      isDebouncing,
      isGuidedTourEnabled,
      setGuidedTourVisited,
      isGuidedTourVisited,
      registerTourStep,
      unregisterTourStep,
      isStepHighestPriority,
    ]
  );

  return (
    <GuidedTourControllerContext.Provider value={contextValue}>
      {children}
    </GuidedTourControllerContext.Provider>
  );
};

// Export for backward compatibility and to avoid refactoring existing code
export const useGuidedTourV2 = () => {
  const { setGuidedTourVisited, isGuidedTourVisited } =
    useGuidedTourControllerContext();

  return {
    setGuidedTourVisited,
    isGuidedTourVisited,
  };
};
